import { apiRequest } from "@/lib/queryClient";
import { trackablePath, redactUrl } from "./trackablePath";

export class PageTrackingService {
  private static instance: PageTrackingService;
  private currentPath: string = '';

  static getInstance() {
    if (!PageTrackingService.instance) {
      PageTrackingService.instance = new PageTrackingService();
    }
    return PageTrackingService.instance;
  }

  private lastRawPath: string = '';
  private untracked = false;

  async trackPageView(rawPath: string, metadata?: Record<string, any>) {
    // Avoid duplicate tracking for same path
    if (this.lastRawPath === rawPath) {
      return;
    }
    this.lastRawPath = rawPath;

    // Never send a public-page token to the server (see trackablePath.ts).
    const path = trackablePath(rawPath);
    this.untracked = path === null;
    this.currentPath = path ?? '';
    if (path === null) {
      return;
    }
    
    try {
      // Only track if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      // Get user info to add role context
      const userInfo = this.getUserInfo();
      
      await apiRequest('/api/activity/track', {
        method: 'POST',
        data: {
          action: 'page_view',
          metadata: {
            path,
            title: document.title,
            timestamp: new Date().toISOString(),
            userRole: userInfo?.role || 'unknown',
            referrer: redactUrl(document.referrer),
            ...metadata
          }
        }
      });
    } catch (error) {
      // Silent fail - don't block user experience
      console.debug('Page tracking failed:', error);
    }
  }

  private getUserInfo() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      // Decode JWT token to get user info
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      return null;
    }
  }

  async trackUserAction(action: string, metadata?: Record<string, any>) {
    // On an untracked page (e.g. /van/day/<token>) — send nothing.
    if (this.untracked) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      await apiRequest('/api/activity/track', {
        method: 'POST',
        data: {
          action,
          metadata: {
            path: this.currentPath,
            timestamp: new Date().toISOString(),
            ...metadata
          }
        }
      });
    } catch (error) {
      console.debug('Action tracking failed:', error);
    }
  }
}

export const pageTrackingService = PageTrackingService.getInstance();